import React, { Fragment, RefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ResourceField, IResourceSaveFormProps } from "../../../interfaces/resources";
import { ISelectDropdownOption } from "../../../interfaces/formElements";
import { ResourceCategory } from "../../../interfaces/types";

import { SelectDropdown, TextArea, TextInput } from "../../reusables/FormElements";
import { SVGIcon } from "../../wrappers/SVGIcon";

import { Icons } from "../../../data/Assets";
import { OPResourceCategories } from "../../../data/options";

import { useCreateResource, useDeleteResource, useUpdateResource, useUploadResourceThumbnail, useDeleteResourceThumbnail } from "../../../hooks/resources";

export const ResourceDetailsForm = (props: IResourceSaveFormProps) => {
    const { operation, isLoading, resourceData, formData, setFormData } = props;

    const navigate = useNavigate();

    const requiredFields: ResourceField[] = ['category', 'title', 'url'];
    const selectFields: ResourceField[] = ['category'];

    const thumbnailFileRef = useRef<HTMLInputElement>(null);

    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);
    const [isThumbnailDeleting, setIsThumbnailDeleting] = useState(false);

    const { mutate: createResource } = useCreateResource();
    const { mutate: updateResource } = useUpdateResource();
    const { mutate: deleteResource } = useDeleteResource();
    const { mutate: deleteThumbnail } = useDeleteResourceThumbnail();
    const { mutate: uploadThumbnail } = useUploadResourceThumbnail();

    useEffect(() => {
        if (resourceData) {
            const category = OPResourceCategories.find(category => category.value === resourceData.category);

            setFormData({
                id: resourceData.id,
                category: { value: { label: category?.label ?? "", value: category?.value ?? "" }, error: '' },
                title: { value: resourceData.title, error: '' },
                description: { value: resourceData.description, error: '' },
                url: { value: resourceData.url, error: '' },
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceData]);

    const onChangeText = (name: ResourceField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: '' },
        });
    };

    const onChangeSelect = (name: ResourceField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            const newData = {
                ...prevData,
                [name]: { value: option, error: "" },
            };

            return newData;
        });
    };

    const getProcessing = () => {
        return isSaving || isThumbnailUploading || isThumbnailDeleting || isDeleting;
    };

    const isSaveDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.category.value.value === (resourceData?.category ?? '') &&
            formData.title.value === (resourceData?.title ?? '') &&
            formData.description.value === (resourceData?.description ?? '') &&
            formData.url.value === (resourceData?.url ?? '');

        return emptyFields || isDataSame;
    };

    const onOpenFile = (fileRef: RefObject<HTMLInputElement>) => {
        if (getProcessing()) return;

        if (fileRef.current) {
            fileRef.current.click();
        };
    };

    const onFileChange = (fileRef: RefObject<HTMLInputElement>) => {
        if (getProcessing()) return;

        if (fileRef.current?.files) {
            const file = fileRef.current.files[0];

            setThumbnailFile(file);

            fileRef.current.value = '';

            if (formData.id) {
                uploadThumbnail({
                    id: formData.id,
                    file,
                    setIsThumbnailUploading,
                    setThumbnailFile,
                });
            };
        };
    };

    const onSaveData = () => {
        if (isSaveDisabled() || getProcessing()) return;

        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id") return;
                newData[key as ResourceField].error = "";
            });

            return newData;
        });

        if (formData.id) {
            updateResource({
                id: formData.id,
                payload: {
                    category: formData.category.value.value as ResourceCategory,
                    title: formData.title.value,
                    description: formData.description.value,
                    url: formData.url.value,
                },
                setIsSaving,
                setThumbnailFile,
            });
        } else {
            createResource({
                payload: {
                    category: formData.category.value.value as ResourceCategory,
                    title: formData.title.value,
                    description: formData.description.value,
                    url: formData.url.value,
                    file: thumbnailFile,
                },
                setIsSaving,
                setThumbnailFile,
            });
        };
    };

    const onDeleteThumbnail = () => {
        if (getProcessing()) return;

        if (!formData.id) {
            setThumbnailFile(null);
        } else {
            deleteThumbnail({
                id: formData.id,
                setIsThumbnailDeleting,
                setThumbnailFile,
            });
        };
    };

    const onDeleteResource = () => {
        if (getProcessing()) return;

        if (!formData.id) {
            setThumbnailFile(null);
        } else {
            deleteResource({
                id: formData.id,
                setIsDeleting,
            });
        };
    };

    return (
        <div className="immersion__details_form">
            <div className="immersion__details_form_inputs__container">
                <div className="immersion__resource_details__upload_actions">
                    <input className="immersion__file_upload_sudo_input" ref={thumbnailFileRef} type="file" accept="image/*" onChange={() => onFileChange(thumbnailFileRef)} />
                    {resourceData?.thumbnail_url
                        ? <div className="immersion__file_upload_preview">
                            <img src={resourceData.thumbnail_url} alt="Thumbnail" />
                            <button
                                className="immersion__file_upload_preview__delete_btn"
                                onClick={onDeleteThumbnail}
                            >
                                {isThumbnailDeleting
                                    ? <i className='immersion__btn_loader' />
                                    : <SVGIcon src={Icons.Trash3Fill} />
                                }
                            </button>
                        </div>
                        : <Fragment>
                            {thumbnailFile && !isThumbnailUploading
                                ? <div className="immersion__file_upload_preview">
                                    <img src={URL.createObjectURL(thumbnailFile)} alt="Thumbnail" />
                                    <button
                                        className="immersion__file_upload_preview__delete_btn"
                                        onClick={onDeleteThumbnail}
                                    >
                                        {isThumbnailDeleting
                                            ? <i className='immersion__btn_loader' />
                                            : <SVGIcon src={Icons.Trash3Fill} />
                                        }
                                    </button>
                                </div>
                                : <button
                                    className="immersion__file_upload_preview immersion__file_upload_preview__upload_btn"
                                    onClick={() => onOpenFile(thumbnailFileRef)}
                                >
                                    {isThumbnailUploading
                                        ? <i className='immersion__btn_loader' />
                                        : <SVGIcon src={Icons.Upload2} />
                                    }
                                    <p className="immersion__file_upload_preview__upload_btn__text">{isThumbnailUploading ? 'Uploading...' : 'Upload Thumbnail'}</p>
                                    <p className="immersion__file_upload_preview__upload_btn__recomm_text">184x141px recommended<br />1.3:1 aspect ratio</p>
                                </button>
                            }
                        </Fragment>
                    }
                </div>
                <div className="immersion__details_form_inputs">
                    <SelectDropdown
                        name="category"
                        label="Category"
                        value={formData.category.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("category", option)}
                        errorMsg={formData.category.error}
                        options={OPResourceCategories}
                        read_only={isLoading}
                        required={true}
                    />
                    <TextInput
                        name="title"
                        label="Title"
                        type="text"
                        value={formData.title.value}
                        onChange={(value: string) => onChangeText('title', value)}
                        errorMsg={formData.title.error}
                        placeholder="Enter title"
                        read_only={isLoading}
                        max_length={200}
                        required={true}
                    />
                    {/* <TextArea
                        name="description"
                        label="Description"
                        value={formData.description.value}
                        onChange={(value: string) => onChangeText('description', value)}
                        errorMsg={formData.description.error}
                        placeholder="Enter description"
                        read_only={isLoading}
                        max_length={500}
                        length_flex={2}
                        required={true}
                    /> */}
                    <TextArea
                        name="url"
                        label="URL"
                        value={formData.url.value}
                        onChange={(value: string) => onChangeText('url', value)}
                        errorMsg={formData.url.error}
                        placeholder="Enter URL"
                        read_only={isLoading}
                        length_flex={2}
                        required={true}
                    />
                </div>
            </div>
            <div className="immersion__details_form_actions">
                <button
                    className={`immersion__btn immersion__btn__secondary ${getProcessing() ? "immersion__btn__secondary__disabled" : ""}`}
                    onClick={() => getProcessing() ? null : navigate('/resources')}
                >
                    {operation === 'create' ? "Discard" : "Go back"}
                </button>
                {operation === 'update' && (
                    <button
                        className={`immersion__btn immersion__btn__primary ${getProcessing() ? "immersion__btn__primary__disabled" : ""}`}
                        onClick={onDeleteResource}
                    >
                        {isDeleting ? <i className='immersion__btn_loader' /> : 'Delete'}
                    </button>
                )}
                <button
                    className={`immersion__btn immersion__btn__primary ${isSaveDisabled() ? "immersion__btn__primary__disabled" : ""}`}
                    onClick={onSaveData}
                >
                    {isSaving ? <i className='immersion__btn_loader' /> : 'Save'}
                </button>
            </div>
        </div>
    );
};